import type { ExportImageOptions } from "@/core/export-image"

export interface DisposablePreparedPhoto {
	dispose(): Promise<void>
}

export function shouldPrefetchPhotoOutputImport(
	options: Pick<ExportImageOptions, "type" | "format">,
) {
	return options.type === "jpg" && options.format === "yuv"
}

interface LookaheadEntry<T extends DisposablePreparedPhoto> {
	index: number
	task: Promise<T>
}

/**
 * Owns at most one prepared photo that has not yet been handed to the render queue.
 * Once `take` returns, disposal becomes the caller's responsibility.
 */
export class PhotoOutputLookahead<Item, Prepared extends DisposablePreparedPhoto> {
	private entry?: LookaheadEntry<Prepared>
	private releasePromise?: Promise<void>

	constructor(
		private readonly items: readonly Item[],
		private readonly prepare: (item: Item, index: number) => Promise<Prepared>,
	) {}

	prefetch(index: number) {
		if (index < 0 || index >= this.items.length || this.releasePromise) {
			return
		}

		if (this.entry?.index === index) {
			return
		}

		if (this.entry) {
			throw new Error(
				`Cannot prepare photo ${index} while photo ${this.entry.index} is still pending`,
			)
		}

		const item = this.items[index]
		if (item === undefined) {
			throw new Error(`Cannot prepare missing photo ${index}`)
		}

		const task = Promise.resolve().then(() => this.prepare(item, index))
		// The consumer awaits the original promise. This prevents an early preparation
		// failure from being reported as unhandled before the render queue reaches it.
		void task.catch(() => undefined)
		this.entry = { index, task }
	}

	async take(index: number): Promise<Prepared> {
		if (this.releasePromise) {
			throw new Error("Cannot consume a released photo-output look-ahead")
		}

		this.prefetch(index)
		const entry = this.entry
		if (!entry || entry.index !== index) {
			throw new Error(`Prepared photo ${index} is unavailable`)
		}

		this.entry = undefined
		return await entry.task
	}

	release() {
		this.releasePromise ??= this.releasePendingEntry()
		return this.releasePromise
	}

	private async releasePendingEntry() {
		const entry = this.entry
		this.entry = undefined
		if (!entry) {
			return
		}

		let prepared: Prepared
		try {
			prepared = await entry.task
		} catch {
			// Preparation owns cleanup for its own failure path.
			return
		}

		await prepared.dispose()
	}
}
